import "bootstrap-daterangepicker/daterangepicker.css";
import "react-datepicker/dist/react-datepicker.css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";
import { Outlet } from "react-router";
import { RouterProvider } from "react-router/dom";

import MaintenanceScreen from "@equidefi/portals/components/MaintenanceScreen";
import { PrivateRoute, PublicRoute } from "@equidefi/portals/components/Routes";
import { useAuth } from "@equidefi/portals/hooks/useAuth";
import { useLogoutReason } from "@equidefi/portals/hooks/useLogoutReason";
import { useCurrentUser, useIdentifyLDUser } from "@equidefi/portals/hooks/useUsers";

import { sentryCreateBrowserRouter } from "@/helpers/sentry";
import CryptoComplete from "@/pages/CryptoComplete";
import WorkflowReinvest from "@/pages/app/vault/v2/WorkflowReinvest";
import { useIntercomMessenger } from "./hooks/useIntercomMessenger";
import ErrorPage from "./pages/ErrorPage";
import NotFound from "./pages/NotFound";
import SupportPageLayout from "./pages/SupportPage";
import SupportPageForm from "./pages/SupportPageForm";
import SupportPageSuccess from "./pages/SupportPageSuccess";
import { TestSigner } from "./pages/agreement-preview/TestSigner";
import InvestmentDetailsWrapper from "./pages/app/vault/InvestmentDetailsWrapper";
import SubpageWrapper from "./pages/app/vault/SubpageWrapper";
import VaultPayment from "./pages/app/vault/VaultPayment";
import VaultLayout from "./pages/app/vault/layout";
import MyProfile from "./pages/app/vault/profile";
import ChangePassword from "./pages/app/vault/v2/ChangePassword";
import AccreditationPage from "./pages/app/vault/v2/InvestmentDetails/pages/Accreditation";
import AgreementsPage from "./pages/app/vault/v2/InvestmentDetails/pages/Agreements";
import InvestmentDetailsPage from "./pages/app/vault/v2/InvestmentDetails/pages/Details";
import PaymentsPage from "./pages/app/vault/v2/InvestmentDetails/pages/Payments";
import PrintPaymentInstructionsPage from "./pages/app/vault/v2/InvestmentDetails/pages/PrintPaymentInstructions";
import InquiryDetailsPage from "./pages/app/vault/v2/InvestmentDetails/pages/inquiry/InquiryDetails";
import InquiryListPage from "./pages/app/vault/v2/InvestmentDetails/pages/inquiry/InquiryList";
import InquiryPersonListPage from "./pages/app/vault/v2/InvestmentDetails/pages/inquiry/InquiryPersonList";
import OfferingInvestmentsView from "./pages/app/vault/v2/OfferingInvestmentsView";
import PortfolioDashboard from "./pages/app/vault/v2/PortfolioDashboard";
import SavedProfileEdit from "./pages/app/vault/v2/SavedProfile/pages/Edit";
import SavedProfileSignerEdit from "./pages/app/vault/v2/SavedProfile/pages/SignerEdit";
import SavedProfileView from "./pages/app/vault/v2/SavedProfile/pages/View";
import SavedProfiles from "./pages/app/vault/v2/SavedProfiles";
import Auth from "./pages/auth";
import Forgot from "./pages/login/forgot";
import Login from "./pages/login/login";
import MFA from "./pages/login/mfa";
import ResetPassword from "./pages/login/reset";
import WorkflowRoot from "./pages/workflow";
import { AdditionalSigner } from "./pages/workflow/AdditionalSigner";
import WorkflowRedirect from "./pages/workflow/WorkflowRedirect";
import { WorkflowAccreditation } from "./pages/workflow/accreditation";
import { WorkflowAddress } from "./pages/workflow/address";
import { WorkflowAgreements } from "./pages/workflow/agreement";
import WorkflowCompleted from "./pages/workflow/complete";
import WorkflowRootError from "./pages/workflow/error";
import { WorkflowKYC } from "./pages/workflow/kyc";
import { WorkflowPayment } from "./pages/workflow/payment";
import { WorkflowPaymentManual } from "./pages/workflow/payment/PaymentManual";
import WorkflowPaymentProcessing from "./pages/workflow/payment/PaymentProcessing";
import { WorkflowProfile } from "./pages/workflow/profile";
import WorkflowRegistration from "./pages/workflow/registration";
import WorkflowChooseInvestment from "./pages/workflow/registration/ChooseInvestment";
import WorkflowLogin from "./pages/workflow/registration/Login";
import WorkflowMFA from "./pages/workflow/registration/MFA";
import WorkflowOpenMfa from "./pages/workflow/registration/OpenMfa";
import { WorkflowSigners } from "./pages/workflow/signers";
import { WorkflowSubscription } from "./pages/workflow/subscription";

import { CONFIG } from "@/constants/config";
import { useEquidefiGAIdentify } from "@/lib/equidefiGAAnalytics";
import { identifyFullview } from "@equidefi/portals/helpers/fullview";

const App = (props) => {
  const { data: me } = useCurrentUser();
  const { isLoggedIn, handleLogout } = useAuth();
  const { setLogoutReason } = useLogoutReason();
  useIdentifyLDUser(me);
  useIntercomMessenger();
  useEquidefiGAIdentify();

  useEffect(() => {
    if (isLoggedIn && me?.is_fraud) {
      setLogoutReason("fraud");
      handleLogout();
      window.location.href = "/";
    }
  }, [isLoggedIn, me?.is_fraud]);

  useEffect(() => {
    identifyFullview(me, CONFIG.ENV);
  }, [me]);

  const flags = useFlags();

  if (flags.maintenanceMode) return <MaintenanceScreen />;

  const router = sentryCreateBrowserRouter(
    [
      {
        path: "/",
        element: (
          <PublicRoute fallbackUrl="/vault/dashboard">
            <Login />
          </PublicRoute>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: "/mfa",
        element: (
          <PublicRoute fallbackUrl="/vault/dashboard">
            <MFA />
          </PublicRoute>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: "/forgot",
        element: (
          <PublicRoute fallbackUrl="/vault/dashboard">
            <Forgot />
          </PublicRoute>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: "/reset/:userId/:token",
        element: (
          <PublicRoute fallbackUrl="/vault/dashboard">
            <ResetPassword />
          </PublicRoute>
        ),
        errorElement: <ErrorPage />,
      },

      {
        path: "/auth/:redirectUrl/:investmentId/:token",
        element: <Auth />,
        errorElement: <ErrorPage />,
      },

      {
        // BVNK "Back to merchant" iframe target — bridges back to the parent
        // workflow via postMessage, no auth or UI of its own (EPP-3368).
        path: "/crypto-complete",
        element: <CryptoComplete />,
        errorElement: <ErrorPage />,
      },

      {
        path: "/support",
        element: <SupportPageLayout />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "",
            element: <SupportPageForm />,
            errorElement: <ErrorPage />,
          },
          {
            path: "success",
            element: <SupportPageSuccess />,
            errorElement: <ErrorPage />,
          },
        ],
      },

      {
        path: "/vault",
        element: (
          <PrivateRoute fallbackUrl="/">
            <VaultLayout />
          </PrivateRoute>
        ),
        errorElement: <ErrorPage />,
        children: [
          {
            index: true,
            path: "dashboard",
            element: <PortfolioDashboard />,
            errorElement: <ErrorPage />,
          },
          {
            path: "offering/:offeringId",
            element: <SubpageWrapper component={OfferingInvestmentsView} />,
            errorElement: <ErrorPage />,
          },
          {
            path: "investment/:id",
            element: <InvestmentDetailsWrapper />,
            errorElement: <ErrorPage />,
          },
          {
            path: "investment/:id/payment",
            element: <VaultPayment />,
          },
          {
            path: "investment/:id/details",
            element: <SubpageWrapper component={InvestmentDetailsPage} />,
            errorElement: <ErrorPage />,
          },
          {
            path: "investment/:id/payments",
            element: <SubpageWrapper component={PaymentsPage} />,
            errorElement: <ErrorPage />,
          },
          {
            path: "investment/:id/payments/print",
            element: <PrintPaymentInstructionsPage />,
            errorElement: <ErrorPage />,
          },
          {
            path: "investment/:id/identity-check",
            element: <SubpageWrapper component={InquiryListPage} />,
            errorElement: <ErrorPage />,
          },
          {
            path: "investment/:id/identity-check/kyc/:userId",
            element: <SubpageWrapper component={InquiryPersonListPage} componentProps={{ isKYB: false }} />,
            errorElement: <ErrorPage />,
          },
          {
            path: "investment/:id/identity-check/kyb/:userId",
            element: <SubpageWrapper component={InquiryPersonListPage} componentProps={{ isKYB: true }} />,
            errorElement: <ErrorPage />,
          },
          {
            path: "investment/:id/identity-check/details/:inquiryId",
            element: <SubpageWrapper component={InquiryDetailsPage} />,
            errorElement: <ErrorPage />,
          },
          {
            path: "investment/:id/accreditation",
            element: <SubpageWrapper component={AccreditationPage} />,
            errorElement: <ErrorPage />,
          },
          {
            path: "investment/:id/agreements",
            element: <SubpageWrapper component={AgreementsPage} />,
            errorElement: <ErrorPage />,
          },
          {
            path: "profile",
            element: <MyProfile />,
            errorElement: <ErrorPage />,
          },
          {
            path: "profile/password",
            element: <ChangePassword />,
            errorElement: <ErrorPage />,
          },
          {
            path: "profile/saved-profiles",
            element: <SavedProfiles />,
            errorElement: <ErrorPage />,
          },
          {
            path: "profile/saved-profiles/:id",
            element: <SavedProfileView />,
            errorElement: <ErrorPage />,
          },
          {
            path: "profile/saved-profiles/:id/edit/:section",
            element: <SavedProfileEdit />,
            errorElement: <ErrorPage />,
          },
          {
            path: "profile/saved-profiles/:id/edit/signers/:signerId",
            element: <SavedProfileSignerEdit />,
            errorElement: <ErrorPage />,
          },
        ],
      },

      {
        path: "/offerings/:slug",
        element: <WorkflowRoot />,
        errorElement: <WorkflowRootError />,
        children: [
          {
            path: "start/:token",
            element: <WorkflowOpenMfa />,
          },
          {
            path: "registration",
            element: <WorkflowRegistration />,
          },
          {
            path: "login",
            element: (
              <PublicRoute fallbackUrl="../choose">
                <WorkflowLogin />
              </PublicRoute>
            ),
          },
          {
            path: "mfa",
            element: <WorkflowMFA />,
          },
          {
            path: "choose",
            element: (
              <PrivateRoute fallbackUrl="../registration">
                <WorkflowChooseInvestment />
              </PrivateRoute>
            ),
          },
          {
            path: "reinvest",
            element: <WorkflowReinvest />,
          },
          {
            path: ":investment_id",
            element: (
              <PrivateRoute fallbackUrl="../login">
                <Outlet />
              </PrivateRoute>
            ),
            children: [
              {
                path: "profile",
                element: <WorkflowProfile />,
              },
              {
                path: "address",
                element: <WorkflowAddress />,
              },
              {
                path: "signers",
                element: <WorkflowSigners />,
              },
              {
                path: "kyc",
                element: <WorkflowKYC />,
              },
              {
                path: "subscription",
                element: <WorkflowSubscription />,
              },
              {
                path: "payment",
                element: <WorkflowPayment />,
              },
              {
                path: "payment/manual",
                element: <WorkflowPaymentManual />,
              },
              {
                path: "payment/processing",
                element: <WorkflowPaymentProcessing />,
              },
              {
                path: "agreement",
                element: <WorkflowAgreements />,
              },
              {
                path: "accreditation",
                element: <WorkflowAccreditation />,
              },
              {
                path: "complete",
                element: <WorkflowCompleted />,
              },
              {
                path: "*",
                element: <WorkflowRedirect />,
              },
            ],
          },
        ],
      },
      {
        path: "/investment/signer/:investmentAgreementId/:signatureId",
        element: <AdditionalSigner />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/agreement/preview/:slug/:token",
        element: <TestSigner />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/offerings/:offeringId/*",
        element: <WorkflowRedirect />,
        errorElement: <ErrorPage />,
      },
      {
        path: "*",
        element: <NotFound />,
        errorElement: <ErrorPage />,
      },
    ],
    {
      v7_relativeSplatPath: false,
    },
  );

  return <RouterProvider router={router} />;
};

export default App;
